import {lazy, Suspense, useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useDecision} from '@s-ui/pde'
import AtomImage from '@s-ui/react-atom-image'
import AtomSkeleton from '@s-ui/react-atom-skeleton'
import AtomSpinner from '@s-ui/react-atom-spinner'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import BannerWebToApp from '@adv-mt/mt-banner-web-to-app'
import ModalBasic, {moleculeModalSizes} from '@adv-mt/mt-modal-basic'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import TrackingAuthentication from '@adv-mt/mt-tracking-authentication'

import VerificationReminder from './components/verification-reminder.js'

const LoginGigya = lazy(() => import(/* webpackChunkName: "LazyLoginGigya" */ '@adv-mt/mt-login-gigya'))

const NO_OP = () => {}

const BASE_CLASS = 'mt-LoginModal'
const FORM_CLASS = `${BASE_CLASS}-form`
const NO_SCREENSET = ''
const IMAGE_SKELETON_WIDTH = '300px'
const IMAGE_SKELETON_HEIGHT = '237px'
const STACKED_IMAGE_PATH = 'images/coches/login-wall.svg'

export default function LoginModal({
  adId,
  enableVerificationReminder = false,
  initialScreen,
  initialStep,
  isOpen,
  onClose = NO_OP,
  onLogin = NO_OP,
  source,
  trackingOrigin,
  hitInformation2
}) {
  const {domain, i18n} = useContext(Context)
  const {enabled: isStackedLayout} = useDecision('ff_web_growth_login_modal_stacked_layout')

  const config = domain.get('config')
  const urlStatics = config.get('urlStatics')
  const {LOGIN_SOURCES, DEFAULT_LOGIN_SOURCE_ID} = config.get('login')
  const {SUGGEST_DEFAULT_LOGIN_ENABLED} = config.get('webToApp')
  const {GIGYA: STEP_GIGYA, VERIFICATION_REMINDER: STEP_VERIFICATION_REMINDER} = config.get('LOGIN_STEPS')

  const [currentStep, setCurrentStep] = useState(initialStep || STEP_GIGYA)
  const [currentScreenset, setCurrentScreenset] = useState('')
  const [pendingUser, setPendingUser] = useState(null)
  const {
    completeRegistration,
    default: defaultLoginScreenId,
    forgot,
    login,
    register,
    reset
  } = config.get('LOGIN_SCREENSET_ID_MAPPING')

  const isVerificationStep = currentStep === STEP_VERIFICATION_REMINDER

  const sourceConfig = LOGIN_SOURCES[source] || LOGIN_SOURCES[DEFAULT_LOGIN_SOURCE_ID]
  const {description, image} = (isVerificationStep && sourceConfig.verification) || sourceConfig

  const valuePropositionImageUrl = `${urlStatics}${image}`

  const handleOnLogin = async ({error, result}) => {
    if (error) return

    const {userData, url} = result

    if (userData.contractId) {
      window.location.href = url
    } else {
      const currentUser = await domain.get('get_current_user_use_case').execute()

      if (enableVerificationReminder && !currentUser.isEmailConfirmed) {
        setPendingUser(currentUser)
        setCurrentStep(STEP_VERIFICATION_REMINDER)
        return
      }

      onLogin({currentUser})
    }
  }

  const handleVerificationReminderContinue = async () => {
    const currentUser = await domain.get('get_current_user_use_case').execute()
    setCurrentStep(STEP_GIGYA)
    onLogin({currentUser})
  }

  const onScreenChange = updatedScreenset => {
    setCurrentScreenset(updatedScreenset)
  }

  const titleMapping = {
    [completeRegistration]: 'LOGIN_FORM_ACTIONS.COMPLETE_SOCIAL_REGISTRATION',
    [defaultLoginScreenId]: 'LOGIN_FORM_ACTIONS.DEFAULT',
    [forgot]: 'LOGIN_FORM_ACTIONS.FORGOT',
    [login]: 'LOGIN_FORM_ACTIONS.LOGIN',
    [NO_SCREENSET]: 'Cargando...',
    [register]: 'LOGIN_FORM_ACTIONS.REGISTER',
    [reset]: 'LOGIN_FORM_ACTIONS.RESET'
  }

  useMount(() => {
    const {unsubscribe} = domain
      .get('login_authentication_use_case')
      .subscribe(({error, result}) => handleOnLogin({error, result}))

    return () => unsubscribe()
  })

  const formWrapperClassNames = cx(FORM_CLASS, {
    [`${FORM_CLASS}--login`]: initialScreen === login,
    [`${FORM_CLASS}--register`]: initialScreen === register
  })

  const title = isVerificationStep
    ? i18n.t('LOGIN_FORM_ACTIONS.VERIFICATION_REMINDER')
    : titleMapping[currentScreenset] && i18n.t(titleMapping[currentScreenset])

  const wrapperClassNames = cx(BASE_CLASS, {
    [`${BASE_CLASS}--stacked`]: isStackedLayout
  })

  const stackedImageUrl = `${urlStatics}${STACKED_IMAGE_PATH}`

  return (
    <ModalBasic
      isInsetBody
      isInsetScrollArea
      isOpen={isOpen}
      onClose={onClose}
      size={isStackedLayout ? moleculeModalSizes.SMALL : moleculeModalSizes.LARGE}
      title={isStackedLayout ? undefined : title}
      className={isStackedLayout ? `${BASE_CLASS}-content--stacked` : undefined}
    >
      <div className={wrapperClassNames}>
        {isStackedLayout ? (
          <>
            <div className={`${BASE_CLASS}-valuePropositionImage ${BASE_CLASS}-valuePropositionImage--stacked`}>
              <AtomImage
                src={stackedImageUrl}
                spinner={<AtomSkeleton width={IMAGE_SKELETON_WIDTH} height={IMAGE_SKELETON_HEIGHT} />}
                alt=""
              />
            </div>
            <TitleBasic title={description} size={titleBasicSizes.s} as={titleBasicTags.p} />
            {isVerificationStep ? (
              <VerificationReminder
                adId={adId}
                currentUser={pendingUser}
                onContinue={handleVerificationReminderContinue}
              />
            ) : (
              <div className={formWrapperClassNames}>
                <Suspense fallback={<AtomSpinner />}>
                  <LoginGigya
                    onScreenChange={onScreenChange}
                    initialScreen={initialScreen}
                    suppressVerificationEmail={enableVerificationReminder}
                  />
                </Suspense>
              </div>
            )}
            {SUGGEST_DEFAULT_LOGIN_ENABLED && (
              <div className={`${BASE_CLASS}-webToAppWrapper`}>
                <BannerWebToApp />
              </div>
            )}
          </>
        ) : (
          <>
            <div className={`${BASE_CLASS}-valueProposition`}>
              {SUGGEST_DEFAULT_LOGIN_ENABLED && (
                <div className={`${BASE_CLASS}-webToAppWrapper`}>
                  <BannerWebToApp />
                </div>
              )}
              <div className={`${BASE_CLASS}-valuePropositionImage`}>
                <AtomImage
                  src={valuePropositionImageUrl}
                  spinner={<AtomSkeleton width={IMAGE_SKELETON_WIDTH} height={IMAGE_SKELETON_HEIGHT} />}
                  alt=""
                />
              </div>
              <TitleBasic title={description} size={titleBasicSizes.s} as={titleBasicTags.p} />
            </div>
            {isVerificationStep ? (
              <VerificationReminder
                adId={adId}
                currentUser={pendingUser}
                onContinue={handleVerificationReminderContinue}
              />
            ) : (
              <div className={formWrapperClassNames}>
                <Suspense fallback={<AtomSpinner />}>
                  <LoginGigya
                    onScreenChange={onScreenChange}
                    initialScreen={initialScreen}
                    suppressVerificationEmail={enableVerificationReminder}
                  />
                </Suspense>
              </div>
            )}
          </>
        )}
      </div>
      <TrackingAuthentication trackingOrigin={trackingOrigin} hitInformation2={hitInformation2} />
    </ModalBasic>
  )
}

LoginModal.displayName = 'LoginModal'
LoginModal.propTypes = {
  adId: PropTypes.string,
  enableVerificationReminder: PropTypes.bool,
  initialScreen: PropTypes.string,
  initialStep: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onLogin: PropTypes.func,
  source: PropTypes.string,
  trackingOrigin: PropTypes.string,
  hitInformation2: PropTypes.string
}
